import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import { Zap, Globe, RefreshCw, Webhook, TestTube, DollarSign, ShieldCheck, AlertCircle, Copy, CheckCheck, Upload, X } from 'lucide-react';
import { toast } from 'sonner';

function CopyableUrl({ label, url, testid }) {
  const [copied, setCopied] = useState(false);
  const onCopy = () => {
    navigator.clipboard.writeText(url || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="flex items-center gap-2 py-1.5">
      <div className="flex-1 min-w-0">
        <p className="text-xs text-slate-500">{label}</p>
        <p className="text-xs font-mono text-slate-800 truncate" data-testid={`url-${testid}`}>{url || '—'}</p>
      </div>
      <Button type="button" variant="outline" size="sm" onClick={onCopy} data-testid={`copy-${testid}`}>
        {copied ? <CheckCheck size={12} /> : <Copy size={12} />}
      </Button>
    </div>
  );
}

function WebhookHelper({ provider }) {
  const [urls, setUrls] = useState(null);
  useEffect(() => { api.get('/admin/branding/urls').then(r => setUrls(r.data)).catch(() => {}); }, []);
  if (!urls) return null;
  return (
    <Card className="bg-slate-50 border border-slate-200 shadow-none rounded-2xl">
      <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-slate-700" style={{ fontFamily: 'Outfit' }}>{provider === 'flutterwave' ? 'Flutterwave Gateway Webhook & Callback' : provider === 'pesapal' ? 'Pesapal Gateway IPN & Callback' : 'Yo! Payments Gateway Status & Callback'}</CardTitle></CardHeader>
      <CardContent className="space-y-1">
        {provider === 'flutterwave' ? (
          <>
            <CopyableUrl label="Webhook URL (set in Flutterwave dashboard)" url={urls.flutterwave_webhook_url} testid="fw-webhook" />
            <CopyableUrl label="Callback / redirect URL" url={urls.flutterwave_callback_url} testid="fw-callback" />
            <p className="text-[11px] text-slate-500 leading-snug pt-1">
              The webhook receives payment status updates from Flutterwave. The callback URL is where the customer returns after payment.
              Both must be set in your Flutterwave dashboard for automatic confirmation.
            </p>
          </>
        ) : provider === 'pesapal' ? (
          <>
            <CopyableUrl label="IPN URL (register this in Pesapal dashboard)" url={urls.pesapal_ipn_url} testid="pp-ipn" />
            <CopyableUrl label="Callback / redirect URL" url={urls.pesapal_callback_url} testid="pp-callback" />
            <p className="text-[11px] text-slate-500 leading-snug pt-1">
              The IPN URL is what you register in Pesapal — Pesapal returns an <strong>IPN&nbsp;ID</strong> that you must paste below.
              The IPN URL and the IPN ID are different values. The callback URL is where the customer returns after payment.
            </p>
          </>
        ) : (
          <>
            <CopyableUrl label="Callback / redirect URL" url={urls.yopayments_callback_url} testid="yo-callback" />
            <CopyableUrl label="Webhook URL" url={urls.yopayments_webhook_url} testid="yo-webhook" />
            <p className="text-[11px] text-slate-500 leading-snug pt-1">
              Yo! Payments can confirm payments through webhook callbacks or by checking status with the transaction reference. Configure the URLs above if your Yo! Payments setup supports webhooks.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function BrandAssetUploader({ title, kind, description, stateKey, testid }) {
  const [branding, setBranding] = useState({});
  const [uploading, setUploading] = useState(false);
  useEffect(() => { api.get('/branding/public').then(r => setBranding(r.data)).catch(() => {}); }, []);
  const url = branding[stateKey] || branding.logo_url || '';
  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) { toast.error('Image must be smaller than 3MB'); return; }
    const fd = new FormData();
    fd.append('file', f);
    fd.append('kind', kind);
    setUploading(true);
    try {
      const r = await api.post('/admin/branding/logo', fd);
      const nextUrl = r.data.logo_url || r.data.public_url || '';
      const nextField = r.data.field || stateKey;
      setBranding(p => ({ ...p, [nextField]: nextUrl }));
      toast.success(`${title} uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };
  const onRemove = async () => {
    try {
      await api.delete(`/admin/branding/logo?kind=${kind}`);
      setBranding(p => ({ ...p, [stateKey]: '' }));
      toast.success(`${title} removed`);
    } catch { toast.error('Remove failed'); }
  };
  return (
    <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
      <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Outfit' }}>{title}</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-slate-500">{description}</p>
        {url ? (
          <div className="flex items-center gap-4">
            <img src={url} alt={`${title} preview`} className="h-14 max-w-[180px] object-contain bg-slate-50 rounded-lg p-2" data-testid={`${testid}-preview`} />
            <Button variant="outline" size="sm" onClick={onRemove} data-testid={`${testid}-remove`}><X size={14} className="mr-1.5" /> Remove</Button>
          </div>
        ) : (
          <p className="text-sm text-slate-400">No image uploaded.</p>
        )}
        <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-700 text-white text-sm font-medium cursor-pointer hover:bg-emerald-800" data-testid={`${testid}-upload-label`}>
          <Upload size={14} />
          {uploading ? 'Uploading…' : (url ? 'Replace image' : 'Upload image')}
          <input type="file" accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml" className="hidden" onChange={onPick} data-testid={`${testid}-input`} />
        </label>
      </CardContent>
    </Card>
  );
}

const ALL_CURRENCIES = [
  "UGX","USD","KES","TZS","RWF","BIF","GBP","EUR","NGN","GHS","ZAR","XAF","XOF","ZMW","MWK","CAD"
];

function FlutterwaveTab() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get('/admin/providers/flutterwave').then(r => setS(r.data)).catch(() => {}); }, []);
  const u = (k, v) => setS(p => ({ ...p, [k]: v }));
  const save = async () => {
    setSaving(true);
    try { await api.put('/admin/providers/flutterwave', s); toast.success('Flutterwave settings saved'); }
    catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };
  if (!s) return <div className="flex items-center justify-center h-40"><div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" /></div>;
  return (
    <div className="space-y-6 max-w-2xl">
      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit' }}>Flutterwave Configuration</CardTitle>
          <div className="flex items-center gap-2">
            <Label className="text-sm">Enable</Label>
            <Switch checked={s.enabled} onCheckedChange={v => u('enabled', v)} data-testid="fw-enable-switch" />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Mode</Label>
              <Select value={s.mode} onValueChange={v => u('mode', v)}>
                <SelectTrigger className="mt-1" data-testid="fw-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="test">Test Mode</SelectItem>
                  <SelectItem value="live">Live Mode</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Default Currency</Label>
              <Select value={s.default_currency} onValueChange={v => u('default_currency', v)}>
                <SelectTrigger className="mt-1" data-testid="fw-currency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ALL_CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="p-3 rounded-xl bg-blue-50 border border-blue-200 text-blue-900 text-xs leading-snug">
            <p className="font-medium mb-1">Choosing a credential mode</p>
            <p>Some Flutterwave sandbox accounts show <strong>Client ID</strong>, <strong>Client Secret</strong> and Encryption Key (the new v4 OAuth flow), while older accounts show the legacy <strong>Public Key</strong> and <strong>Secret Key</strong>. Choose the credential type shown in your Flutterwave dashboard.</p>
          </div>
          <div>
            <Label>Credential mode</Label>
            <Select value={s.credential_mode || 'legacy'} onValueChange={v => u('credential_mode', v)}>
              <SelectTrigger className="mt-1" data-testid="fw-credential-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="legacy">Legacy API keys (Public + Secret)</SelectItem>
                <SelectItem value="client_credentials">Client credentials (Client ID + Client Secret)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {(s.credential_mode || 'legacy') === 'legacy' ? (
            <>
              <div>
                <Label>Public Key</Label>
                <Input data-testid="fw-public-key" value={s.public_key || ''} onChange={e => u('public_key', e.target.value)} placeholder="FLWPUBK-... or FLWPUBK_TEST-..." className="mt-1 font-mono text-xs" />
              </div>
              <div>
                <Label>Secret Key {s.has_secret_key && <span className="text-emerald-600 text-xs ml-1">(configured)</span>}</Label>
                <Input data-testid="fw-secret-key" type="password" value={s.secret_key || ''} onChange={e => u('secret_key', e.target.value)} placeholder={s.has_secret_key ? 'Leave blank to keep current' : 'FLWSECK-... or FLWSECK_TEST-...'} className="mt-1 font-mono text-xs" />
              </div>
            </>
          ) : (
            <>
              <div>
                <Label>Client ID</Label>
                <Input data-testid="fw-client-id" value={s.client_id || ''} onChange={e => u('client_id', e.target.value)} placeholder="e.g. d845591c-e0b9-406e-bfa4-..." className="mt-1 font-mono text-xs" />
              </div>
              <div>
                <Label>Client Secret {s.has_client_secret && <span className="text-emerald-600 text-xs ml-1">(configured)</span>}</Label>
                <Input data-testid="fw-client-secret" type="password" value={s.client_secret || ''} onChange={e => u('client_secret', e.target.value)} placeholder={s.has_client_secret ? 'Leave blank to keep current' : 'Client secret from Flutterwave dashboard'} className="mt-1 font-mono text-xs" />
              </div>
              <p className="text-[11px] text-slate-500 -mt-2">In v4 OAuth mode, Jjuma Pay exchanges these for a short-lived access token (10-min) on every checkout. Client Secret is never exposed to the frontend.</p>
            </>
          )}
          <div>
            <Label>Encryption Key {s.has_encryption_key && <span className="text-emerald-600 text-xs ml-1">(configured)</span>}</Label>
            <Input data-testid="fw-enc-key" type="password" value={s.encryption_key || ''} onChange={e => u('encryption_key', e.target.value)} placeholder={s.has_encryption_key ? 'Leave blank to keep current' : 'Encryption key'} className="mt-1 font-mono text-xs" />
          </div>
          <div>
            <Label>Webhook Secret/Hash {s.has_webhook_secret && <span className="text-emerald-600 text-xs ml-1">(configured)</span>}</Label>
            <Input data-testid="fw-webhook-secret" type="password" value={s.webhook_secret || ''} onChange={e => u('webhook_secret', e.target.value)} placeholder={s.has_webhook_secret ? 'Leave blank to keep current' : 'Webhook verification hash'} className="mt-1 font-mono text-xs" />
          </div>
          <div>
            <Label>Callback URL</Label>
            <Input data-testid="fw-callback-url" value={s.callback_url || ''} onChange={e => u('callback_url', e.target.value)} placeholder="https://pay.jjuma.com/pay/<slug>/callback" className="mt-1 text-sm" />
          </div>
          <div>
            <Label>Webhook URL</Label>
            <Input data-testid="fw-webhook-url" value={s.webhook_url || ''} onChange={e => u('webhook_url', e.target.value)} placeholder="https://api.jjuma.com/api/webhooks/flutterwave" className="mt-1 text-sm" />
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
            <p className="text-sm font-semibold text-slate-900">Payout API (optional)</p>
            <p className="text-xs text-slate-500">Use these fields to configure the gateway payout endpoint without exposing any secret key in the browser.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Payout API URL</Label>
                <Input value={s.payout_api_url || ''} onChange={e => u('payout_api_url', e.target.value)} placeholder="https://api.provider.com/payouts" className="mt-1 text-sm" data-testid="fw-payout-url" />
              </div>
              <div>
                <Label className="text-xs">Payout API Method</Label>
                <Select value={s.payout_api_method || 'POST'} onValueChange={v => u('payout_api_method', v)}>
                  <SelectTrigger className="mt-1" data-testid="fw-payout-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="POST">POST</SelectItem>
                    <SelectItem value="GET">GET</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Auth Header</Label>
                <Input value={s.payout_auth_header || 'Authorization'} onChange={e => u('payout_auth_header', e.target.value)} className="mt-1 text-sm" data-testid="fw-payout-auth-header" />
              </div>
              <div>
                <Label className="text-xs">Auth Prefix</Label>
                <Input value={s.payout_auth_prefix || 'Bearer'} onChange={e => u('payout_auth_prefix', e.target.value)} className="mt-1 text-sm" data-testid="fw-payout-auth-prefix" />
              </div>
              <div>
                <Label className="text-xs">Status URL Template</Label>
                <Input value={s.payout_status_url || ''} onChange={e => u('payout_status_url', e.target.value)} placeholder="https://api.provider.com/payouts/{reference}" className="mt-1 text-sm" data-testid="fw-payout-status-url" />
              </div>
              <div>
                <Label className="text-xs">Status Method</Label>
                <Select value={s.payout_status_method || 'GET'} onValueChange={v => u('payout_status_method', v)}>
                  <SelectTrigger className="mt-1" data-testid="fw-payout-status-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GET">GET</SelectItem>
                    <SelectItem value="POST">POST</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <Button onClick={save} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="fw-save-btn">
            {saving ? 'Saving...' : 'Save Flutterwave Settings'}
          </Button>
        </CardContent>
      </Card>
      <WebhookHelper provider="flutterwave" />
    </div>
  );
}

function PesapalTab() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get('/admin/providers/pesapal').then(r => setS(r.data)).catch(() => {}); }, []);
  const u = (k, v) => setS(p => ({ ...p, [k]: v }));
  const save = async () => {
    setSaving(true);
    try { await api.put('/admin/providers/pesapal', s); toast.success('Pesapal settings saved'); }
    catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };
  if (!s) return <div className="flex items-center justify-center h-40"><div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" /></div>;
  return (
    <div className="space-y-6 max-w-2xl">
      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit' }}>Pesapal Configuration</CardTitle>
          <div className="flex items-center gap-2">
            <Label className="text-sm">Enable</Label>
            <Switch checked={s.enabled} onCheckedChange={v => u('enabled', v)} data-testid="pp-enable-switch" />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Mode</Label>
              <Select value={s.mode} onValueChange={v => u('mode', v)}>
                <SelectTrigger className="mt-1" data-testid="pp-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="test">Test Mode</SelectItem>
                  <SelectItem value="live">Live Mode</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Default Currency</Label>
              <Select value={s.default_currency} onValueChange={v => u('default_currency', v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["UGX","KES","TZS","RWF","USD"].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Consumer Key</Label>
            <Input data-testid="pp-consumer-key" value={s.consumer_key || ''} onChange={e => u('consumer_key', e.target.value)} placeholder="Consumer key" className="mt-1 font-mono text-xs" />
          </div>
          <div>
            <Label>Consumer Secret {s.has_consumer_secret && <span className="text-emerald-600 text-xs ml-1">(configured)</span>}</Label>
            <Input data-testid="pp-consumer-secret" type="password" value={s.consumer_secret || ''} onChange={e => u('consumer_secret', e.target.value)} placeholder={s.has_consumer_secret ? 'Leave blank to keep current' : 'Consumer secret'} className="mt-1 font-mono text-xs" />
          </div>
          <div>
            <Label>IPN/Webhook URL</Label>
            <Input data-testid="pp-ipn-url" value={s.ipn_url || ''} onChange={e => u('ipn_url', e.target.value)} placeholder="https://api.jjuma.com/api/webhooks/pesapal" className="mt-1 text-sm" />
            <p className="text-[11px] text-slate-500 mt-1">The URL Pesapal will POST notifications to. Register this URL in Pesapal — they will return an <strong>IPN&nbsp;ID</strong> to paste below.</p>
          </div>
          <div>
            <Label>Pesapal IPN ID</Label>
            <div className="flex gap-2 mt-1">
              <Input data-testid="pp-ipn-id" value={s.ipn_id || ''} onChange={e => u('ipn_id', e.target.value)} placeholder="e.g. 9d5bf245-1a7e-4b9c-…" className="font-mono text-xs flex-1" />
              <Button
                type="button"
                variant="outline"
                onClick={async () => {
                  try {
                    const r = await api.post('/admin/providers/pesapal/register-ipn', { ipn_url: s.ipn_url });
                    u('ipn_id', r.data.ipn_id);
                    toast.success('IPN registered with Pesapal');
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'IPN registration failed');
                  }
                }}
                data-testid="pp-register-ipn-btn"
              >
                Register IPN
              </Button>
            </div>
            <p className="text-[11px] text-slate-500 mt-1">If the registration button is unavailable, register the IPN URL in the Pesapal dashboard manually and paste the returned IPN ID here.</p>
          </div>
          <div>
            <Label>Callback URL</Label>
            <Input data-testid="pp-callback-url" value={s.callback_url || ''} onChange={e => u('callback_url', e.target.value)} placeholder="https://pay.jjuma.com/pay/<slug>/callback" className="mt-1 text-sm" />
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
            <p className="text-sm font-semibold text-slate-900">Payout API (optional)</p>
            <p className="text-xs text-slate-500">Use these fields to configure the gateway payout endpoint without exposing any secret key in the browser.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Payout API URL</Label>
                <Input value={s.payout_api_url || ''} onChange={e => u('payout_api_url', e.target.value)} placeholder="https://api.provider.com/payouts" className="mt-1 text-sm" data-testid="pp-payout-url" />
              </div>
              <div>
                <Label className="text-xs">Payout API Method</Label>
                <Select value={s.payout_api_method || 'POST'} onValueChange={v => u('payout_api_method', v)}>
                  <SelectTrigger className="mt-1" data-testid="pp-payout-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="POST">POST</SelectItem>
                    <SelectItem value="GET">GET</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Auth Header</Label>
                <Input value={s.payout_auth_header || 'Authorization'} onChange={e => u('payout_auth_header', e.target.value)} className="mt-1 text-sm" data-testid="pp-payout-auth-header" />
              </div>
              <div>
                <Label className="text-xs">Auth Prefix</Label>
                <Input value={s.payout_auth_prefix || 'Bearer'} onChange={e => u('payout_auth_prefix', e.target.value)} className="mt-1 text-sm" data-testid="pp-payout-auth-prefix" />
              </div>
              <div>
                <Label className="text-xs">Status URL Template</Label>
                <Input value={s.payout_status_url || ''} onChange={e => u('payout_status_url', e.target.value)} placeholder="https://api.provider.com/payouts/{reference}" className="mt-1 text-sm" data-testid="pp-payout-status-url" />
              </div>
              <div>
                <Label className="text-xs">Status Method</Label>
                <Select value={s.payout_status_method || 'GET'} onValueChange={v => u('payout_status_method', v)}>
                  <SelectTrigger className="mt-1" data-testid="pp-payout-status-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GET">GET</SelectItem>
                    <SelectItem value="POST">POST</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <Button onClick={save} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="pp-save-btn">
            {saving ? 'Saving...' : 'Save Pesapal Settings'}
          </Button>
        </CardContent>
      </Card>
      <WebhookHelper provider="pesapal" />
    </div>
  );
}

function YoPaymentsTab() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get('/admin/providers/yopayments').then(r => setS(r.data)).catch(() => {}); }, []);
  const u = (k, v) => setS(p => ({ ...p, [k]: v }));
  const save = async () => {
    setSaving(true);
    try { await api.put('/admin/providers/yopayments', s); toast.success('Yo! Payments settings saved'); }
    catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };
  if (!s) return <div className="flex items-center justify-center h-40"><div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" /></div>;
  return (
    <div className="space-y-6 max-w-2xl">
      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit' }}>Yo! Payments Configuration</CardTitle>
          <div className="flex items-center gap-2">
            <Label className="text-sm">Enable</Label>
            <Switch checked={s.enabled} onCheckedChange={v => u('enabled', v)} data-testid="yo-enable-switch" />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Mode</Label>
              <Select value={s.mode || 'sandbox'} onValueChange={v => u('mode', v)}>
                <SelectTrigger className="mt-1" data-testid="yo-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sandbox">Sandbox</SelectItem>
                  <SelectItem value="production">Production</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Default Currency</Label>
              <Select value={s.default_currency || 'UGX'} onValueChange={v => u('default_currency', v)}>
                <SelectTrigger className="mt-1" data-testid="yo-currency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["UGX","KES","TZS","RWF","USD"].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 leading-snug">
            Enter the exact Yo! Payments API base URL and endpoint paths from your Yo! Payments account or API documentation.
          </div>
          <div>
            <Label>Username</Label>
            <Input data-testid="yo-username" value={s.username || ''} onChange={e => u('username', e.target.value)} placeholder="Yo! Payments API username" className="mt-1 font-mono text-xs" />
          </div>
          <div>
            <Label>Password {s.has_password && <span className="text-emerald-600 text-xs ml-1">(configured)</span>}</Label>
            <Input data-testid="yo-password" type="password" value={s.password || ''} onChange={e => u('password', e.target.value)} placeholder={s.has_password ? 'Leave blank to keep current' : 'Yo! Payments API password'} className="mt-1 font-mono text-xs" />
          </div>
          <div>
            <Label>Webhook/IPN Secret {s.has_webhook_secret && <span className="text-emerald-600 text-xs ml-1">(configured)</span>}</Label>
            <Input data-testid="yo-webhook-secret" type="password" value={s.webhook_secret || ''} onChange={e => u('webhook_secret', e.target.value)} placeholder={s.has_webhook_secret ? 'Leave blank to keep current' : 'Yo! Payments webhook/IPN secret'} className="mt-1 font-mono text-xs" />
          </div>
          <div>
            <Label>Supported Countries</Label>
            <Input data-testid="yo-countries" value={Array.isArray(s.supported_countries) ? s.supported_countries.join(', ') : (s.supported_countries || '')} onChange={e => u('supported_countries', e.target.value)} placeholder="Uganda, Kenya" className="mt-1 text-sm" />
          </div>
          <div>
            <Label>Supported Currencies</Label>
            <Input data-testid="yo-currencies" value={Array.isArray(s.supported_currencies) ? s.supported_currencies.join(', ') : (s.supported_currencies || '')} onChange={e => u('supported_currencies', e.target.value)} placeholder="UGX, KES" className="mt-1 text-sm" />
          </div>
          <div>
            <Label>Supported Payment Methods</Label>
            <Input data-testid="yo-methods" value={Array.isArray(s.supported_payment_methods) ? s.supported_payment_methods.join(', ') : (s.supported_payment_methods || '')} onChange={e => u('supported_payment_methods', e.target.value)} placeholder="mtn_mobile_money, airtel_money" className="mt-1 text-sm" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>API Base URL</Label>
              <Input data-testid="yo-api-base-url" value={s.api_base_url || ''} onChange={e => u('api_base_url', e.target.value)} placeholder="https://example-yopayments-api-url" className="mt-1 text-sm" />
            </div>
            <div>
              <Label>Deposit Endpoint</Label>
              <Input data-testid="yo-deposit-endpoint" value={s.deposit_endpoint || ''} onChange={e => u('deposit_endpoint', e.target.value)} placeholder="/deposit or /acdepositfunds" className="mt-1 text-sm" />
            </div>
            <div>
              <Label>Status Endpoint</Label>
              <Input data-testid="yo-status-endpoint" value={s.status_endpoint || ''} onChange={e => u('status_endpoint', e.target.value)} placeholder="/status or /transaction-status" className="mt-1 text-sm" />
            </div>
            <div>
              <Label>Callback URL</Label>
              <Input data-testid="yo-callback-url" value={s.callback_url || ''} onChange={e => u('callback_url', e.target.value)} placeholder="https://api.jjuma.com/api/webhooks/yopayments" className="mt-1 text-sm" />
            </div>
            <div>
              <Label>Webhook URL</Label>
              <Input data-testid="yo-webhook-url" value={s.webhook_url || ''} onChange={e => u('webhook_url', e.target.value)} placeholder="https://api.jjuma.com/api/webhooks/yopayments" className="mt-1 text-sm" />
            </div>
            <p className="sm:col-span-2 text-[11px] text-slate-500 leading-snug">
              Use the callback URL for the customer return page after payment and the webhook URL for payment status updates from Yo! Payments, if your account supports webhooks.
            </p>
          </div>
          <Button onClick={save} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="yo-save-btn">
            {saving ? 'Saving...' : 'Save Yo! Payments Settings'}
          </Button>
        </CardContent>
      </Card>
      <WebhookHelper provider="yopayments" />
    </div>
  );
}

function CurrenciesTab() {
  const [currencies, setCurrencies] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get('/admin/providers/currencies').then(r => setCurrencies(r.data)).catch(() => {}); }, []);
  const toggle = (code) => {
    setCurrencies(prev => prev.map(c => c.code === code ? { ...c, enabled: !c.enabled } : c));
  };
  const save = async () => {
    setSaving(true);
    const enabled = currencies.filter(c => c.enabled).map(c => c.code);
    try { await api.put('/admin/providers/currencies', { enabled_currencies: enabled }); toast.success('Currencies updated'); }
    catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-4">
      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Outfit' }}>Manage Currencies</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {currencies.map(c => (
              <label key={c.code} className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer transition-colors ${c.enabled ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`} data-testid={`currency-toggle-${c.code}`}>
                <Checkbox checked={c.enabled} onCheckedChange={() => toggle(c.code)} />
                <div>
                  <p className="text-sm font-semibold text-slate-900">{c.code}</p>
                  <p className="text-xs text-slate-500">{c.name}</p>
                </div>
              </label>
            ))}
          </div>
          <Button onClick={save} disabled={saving} className="mt-4 bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="save-currencies-btn">
            {saving ? 'Saving...' : 'Save Currency Settings'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function ConversionTab() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get('/admin/providers/conversion').then(r => setS(r.data)).catch(() => {}); }, []);
  const u = (k, v) => setS(p => ({ ...p, [k]: v }));
  const updateRate = (pair, value) => {
    setS(p => ({ ...p, exchange_rates: { ...p.exchange_rates, [pair]: parseFloat(value) || 0 } }));
  };
  const save = async () => {
    setSaving(true);
    try { await api.put('/admin/providers/conversion', s); toast.success('Conversion settings saved'); }
    catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };
  if (!s) return <div className="flex items-center justify-center h-40"><div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" /></div>;
  return (
    <div className="space-y-6 max-w-2xl">
      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Outfit' }}>Conversion Settings</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Base Currency</Label>
              <Select value={s.base_currency} onValueChange={v => u('base_currency', v)}>
                <SelectTrigger className="mt-1" data-testid="conv-base-currency"><SelectValue /></SelectTrigger>
                <SelectContent>{ALL_CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Settlement Currency</Label>
              <Select value={s.settlement_currency} onValueChange={v => u('settlement_currency', v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{ALL_CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Conversion Margin (%)</Label>
              <Input type="number" step="0.1" value={s.conversion_margin_pct} onChange={e => u('conversion_margin_pct', parseFloat(e.target.value))} className="mt-1" data-testid="conv-margin" />
            </div>
            <div className="flex items-end gap-2">
              <div className="flex items-center gap-2">
                <Switch checked={s.auto_conversion_enabled} onCheckedChange={v => u('auto_conversion_enabled', v)} data-testid="conv-auto-switch" />
                <Label className="text-sm">Auto conversion (placeholder)</Label>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Outfit' }}>Manual Exchange Rates (to {s.base_currency})</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {Object.entries(s.exchange_rates || {}).map(([pair, rate]) => (
              <div key={pair}>
                <Label className="text-xs text-slate-500">{pair.replace('_', ' to ')}</Label>
                <Input type="number" step="0.01" value={rate} onChange={e => updateRate(pair, e.target.value)} className="mt-1 font-mono text-sm" data-testid={`rate-${pair}`} />
              </div>
            ))}
          </div>
          <Button onClick={save} disabled={saving} className="mt-4 bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="save-conversion-btn">
            {saving ? 'Saving...' : 'Save Conversion Settings'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function WebhooksTab() {
  const [logs, setLogs] = useState({ logs: [], total: 0, pages: 1 });
  const [provider, setProvider] = useState('all');
  const [page, setPage] = useState(1);
  const fetchLogs = useCallback(() => {
    const params = new URLSearchParams({ page, limit: 15 });
    if (provider !== 'all') params.set('provider', provider);
    api.get(`/admin/providers/webhook-logs?${params}`).then(r => setLogs(r.data)).catch(() => {});
  }, [provider, page]);
  useEffect(() => { fetchLogs(); }, [fetchLogs]);
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Select value={provider} onValueChange={v => { setProvider(v); setPage(1); }}>
          <SelectTrigger className="w-40" data-testid="webhook-provider-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Gateways</SelectItem>
            <SelectItem value="flutterwave">Flutterwave</SelectItem>
          <SelectItem value="pesapal">Pesapal</SelectItem>
          <SelectItem value="yopayments">Yo! Payments</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={fetchLogs} data-testid="refresh-webhook-logs"><RefreshCw size={14} className="mr-1" /> Refresh</Button>
        <span className="text-sm text-slate-500">{logs.total} total logs</span>
      </div>
      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardContent className="p-0 overflow-x-auto">
          {logs.logs.length === 0 ? (
            <div className="py-12 text-center">
              <Webhook size={36} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 text-sm">No webhook logs yet</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Gateway</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Event</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Status</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">TXN ID</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.logs.map((l, i) => (
                  <TableRow key={i} className="hover:bg-slate-50/50" data-testid={`webhook-log-${i}`}>
                    <TableCell><Badge variant="outline" className="capitalize">{l.provider}</Badge></TableCell>
                    <TableCell className="text-sm text-slate-700">{l.event_type}</TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${l.status === 'processed' ? 'bg-emerald-100 text-emerald-800' : l.status === 'error' ? 'bg-red-100 text-red-800' : 'bg-slate-100 text-slate-600'}`}>{l.status}</span>
                    </TableCell>
                    <TableCell className="text-xs mono-nums text-slate-500">{l.transaction_id || '-'}</TableCell>
                    <TableCell className="text-xs text-slate-500">{new Date(l.created_at).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      {logs.pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1.5 text-sm rounded-lg border bg-white hover:bg-slate-50 disabled:opacity-40">Previous</button>
          <span className="text-sm text-slate-500">Page {page} of {logs.pages}</span>
          <button onClick={() => setPage(p => Math.min(logs.pages, p + 1))} disabled={page === logs.pages} className="px-3 py-1.5 text-sm rounded-lg border bg-white hover:bg-slate-50 disabled:opacity-40">Next</button>
        </div>
      )}
    </div>
  );
}

function TestModeTab() {
  const [status, setStatus] = useState(null);
  const [settings, setSettings] = useState(null);
  const [savingDefault, setSavingDefault] = useState(false);
  useEffect(() => {
    api.get('/admin/providers/status').then(r => setStatus(r.data)).catch(() => {});
    api.get('/admin/settings').then(r => setSettings(r.data)).catch(() => {});
  }, []);
  const saveDefault = async () => {
    if (!settings) return;
    setSavingDefault(true);
    try {
      await api.put('/admin/settings', { default_payment_provider: effectiveDefault });
      toast.success('Default gateway saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save default gateway');
    } finally {
      setSavingDefault(false);
    }
  };
  const enabledProviders = status?.enabled_providers || [];
  const effectiveDefault = settings?.default_payment_provider && settings.default_payment_provider !== 'auto'
    ? settings.default_payment_provider
    : (status?.default_provider || 'auto');
  return (
    <div className="space-y-6 max-w-2xl">
      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Outfit' }}>Gateway Status</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {status ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {Object.entries(status).filter(([name, info]) => name === 'flutterwave' || name === 'pesapal' || name === 'yopayments').map(([name, info]) => (
                <div key={name} className={`p-4 rounded-xl border ${info.enabled ? 'border-emerald-300 bg-emerald-50/50' : 'border-slate-200'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-semibold text-slate-900 capitalize">{name}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${info.enabled ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>
                      {info.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500">Mode: <span className={`font-medium ${info.mode === 'live' ? 'text-emerald-700' : 'text-amber-600'}`}>{info.mode}</span></p>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-slate-500">Loading...</p>}
          {status?.warning && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              {status.warning}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit' }}>Default Gateway</CardTitle>
          <p className="text-xs text-slate-500 mt-1">Default gateway is used when users select Auto. Disabled gateways will not appear in payment link forms or checkout.</p>
        </CardHeader>
        <CardContent className="space-y-3">
          {settings ? (
            enabledProviders.length > 1 ? (
              <>
                <div>
                  <Label className="text-xs">Default payment gateway</Label>
                  <Select
                    value={effectiveDefault}
                    onValueChange={v => setSettings(prev => ({ ...prev, default_payment_provider: v }))}
                  >
                    <SelectTrigger className="mt-1" data-testid="default-gateway-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">{status?.auto_label || 'Auto (Best Available)'}</SelectItem>
                      {enabledProviders.map(p => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={saveDefault} disabled={savingDefault} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="save-default-gateway-btn">
                  {savingDefault ? 'Saving...' : 'Save Default Gateway'}
                </Button>
              </>
            ) : enabledProviders.length === 1 ? (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-sm font-semibold text-slate-900">Default gateway locked</p>
                <p className="text-sm text-slate-600 mt-1">{enabledProviders[0].label} is the only enabled gateway, so Auto resolves to it automatically.</p>
              </div>
            ) : (
              <p className="text-sm text-slate-500">Enable at least one gateway before choosing a default.</p>
            )
          ) : <p className="text-sm text-slate-500">Loading...</p>}
          <p className="text-[11px] text-slate-500">Auto uses the selected default gateway, and disabled gateways are hidden from payment link forms and checkout.</p>
        </CardContent>
      </Card>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Outfit' }}>Test Mode Information</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-blue-800 text-sm flex items-start gap-2">
            <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium">Test Mode</p>
              <p className="mt-1">When gateways are in test mode, payments are simulated. No real money is transferred. Use gateway sandbox/test keys for testing.</p>
            </div>
          </div>
          <div className="space-y-2 text-sm text-slate-600">
            <p><strong>Flutterwave Test:</strong> Use test keys from <a href="https://dashboard.flutterwave.com" target="_blank" rel="noreferrer" className="text-emerald-700 hover:underline">Flutterwave Dashboard</a></p>
            <p><strong>Pesapal Sandbox:</strong> Use demo keys from <a href="https://dashboard.pesapal.com" target="_blank" rel="noreferrer" className="text-emerald-700 hover:underline">Pesapal Dashboard</a></p>
            <p><strong>Yo! Payments:</strong> Configure the API base URL, username, and password for your Yo! Payments account.</p>
            <p><strong>Jjuma Pay Simulation:</strong> Without gateway keys, payments are recorded with simulated success status.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function PaymentProviders() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit' }}>Payment Gateways</h1>
        <p className="text-slate-500 text-sm mt-0.5">Configure Flutterwave, Pesapal, currencies, and conversion settings</p>
      </div>
      <Tabs defaultValue="flutterwave">
        <TabsList className="mb-4 flex-wrap" data-testid="providers-tabs">
          <TabsTrigger value="flutterwave" data-testid="providers-tab-flutterwave"><Zap size={14} className="mr-1" /> Flutterwave</TabsTrigger>
          <TabsTrigger value="pesapal" data-testid="providers-tab-pesapal"><Globe size={14} className="mr-1" /> Pesapal</TabsTrigger>
          <TabsTrigger value="yopayments" data-testid="providers-tab-yopayments"><ShieldCheck size={14} className="mr-1" /> Yo! Payments</TabsTrigger>
          <TabsTrigger value="currencies" data-testid="providers-tab-currencies"><DollarSign size={14} className="mr-1" /> Currencies</TabsTrigger>
          <TabsTrigger value="conversion" data-testid="providers-tab-conversion"><RefreshCw size={14} className="mr-1" /> Conversion</TabsTrigger>
          <TabsTrigger value="webhooks" data-testid="providers-tab-webhooks"><Webhook size={14} className="mr-1" /> Webhooks</TabsTrigger>
          <TabsTrigger value="testmode" data-testid="providers-tab-testmode"><TestTube size={14} className="mr-1" /> Test Mode</TabsTrigger>
          <TabsTrigger value="branding" data-testid="providers-tab-branding"><ShieldCheck size={14} className="mr-1" /> Branding</TabsTrigger>
        </TabsList>
        <TabsContent value="flutterwave"><FlutterwaveTab /></TabsContent>
        <TabsContent value="pesapal"><PesapalTab /></TabsContent>
        <TabsContent value="yopayments"><YoPaymentsTab /></TabsContent>
        <TabsContent value="currencies"><CurrenciesTab /></TabsContent>
        <TabsContent value="conversion"><ConversionTab /></TabsContent>
        <TabsContent value="webhooks"><WebhooksTab /></TabsContent>
        <TabsContent value="testmode"><TestModeTab /></TabsContent>
        <TabsContent value="branding">
          <div className="grid gap-4">
            <BrandAssetUploader
              title="Jjuma Pay Main Logo"
              kind="logo"
              stateKey="logo_url"
              testid="brand-logo"
              description="Primary platform logo used in the public branding fallback and general platform header."
            />
            <BrandAssetUploader
              title="Checkout Logo"
              kind="checkout"
              stateKey="checkout_logo_url"
              testid="brand-checkout-logo"
              description="Used on hosted checkout pages and payment link views."
            />
            <BrandAssetUploader
              title="Receipt Logo"
              kind="receipt"
              stateKey="receipt_logo_url"
              testid="brand-receipt-logo"
              description="Used on receipts and success screens."
            />
            <BrandAssetUploader
              title="Favicon"
              kind="favicon"
              stateKey="favicon_url"
              testid="brand-favicon"
              description="Small icon for browser tabs and bookmarks."
            />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
